/**
 * Referral program types and business logic manager.
 * Backed by ReferralService (FirstPromoter integration via Routemize backend).
 */

import { ReferralService } from '@/api/services/ReferralService';
import type { Routemize_DTOs_ReferralProgramDto } from '@/api/models/Routemize_DTOs_ReferralProgramDto';
import type { Routemize_DTOs_UpdatePayPalEmailDto } from '@/api/models/Routemize_DTOs_UpdatePayPalEmailDto';

// ============================================================================
// Frontend types — reconciled with generated DTOs (source of truth)
// ============================================================================

/**
 * status comes back as a free string from FirstPromoter (e.g. "pending",
 * "qualified", "converted", "expired"). Keep it as `string` so unknown FP
 * values don't hard-fail. Call getStatusDisplay() to get a safe UI label.
 */
export interface Referral {
  id: string;
  referredEmail: string;
  referredName: string;
  /** Parsed from ISO string at the Manager boundary; null when FP omits it */
  signupDate: Date | null;
  /** Free string from FirstPromoter — do not narrow to a union */
  status: string;
  earnedAmount: number;
  qualificationProgress: number; // 0-100
  /** Parsed from ISO string at the Manager boundary; undefined when not yet converted */
  conversionDate?: Date;
}

export interface ReferralStats {
  totalReferrals: number;
  qualifiedReferrals: number;
  convertedReferrals: number;
  pendingEarnings: number;
  totalEarnings: number;
  availableForPayout: number;
  lifetimePayouts: number;
}

export interface ReferralProgramData {
  isEnrolled: boolean;
  referralLink: string | null;
  referralCode: string | null;
  stats: ReferralStats;
  referrals: Referral[];
  payPalEmail: string | null;
}

// ============================================================================
// ReferralManager
// ============================================================================

export class ReferralManager {

  /**
   * Fetch the full referral program data for the current tenant.
   * Parses date strings → Date at the boundary so callers get typed Dates.
   */
  async getReferralProgram(): Promise<ReferralProgramData> {
    const dto = await ReferralService.getApiAppReferralGetMyReferralProgram();
    return this.mapProgram(dto);
  }

  /**
   * Enroll the current tenant as a FirstPromoter promoter on demand (self-service
   * from the Refer page). Idempotent — returns the refreshed program either way.
   */
  async enroll(): Promise<ReferralProgramData> {
    const dto = await ReferralService.postApiAppReferralEnroll();
    return this.mapProgram(dto);
  }

  /** Map the generated ReferralProgramDto → the frontend ReferralProgramData shape. */
  private mapProgram(dto: Routemize_DTOs_ReferralProgramDto): ReferralProgramData {
    const stats: ReferralStats = {
      totalReferrals: dto.stats?.totalReferrals ?? 0,
      qualifiedReferrals: dto.stats?.qualifiedReferrals ?? 0,
      convertedReferrals: dto.stats?.convertedReferrals ?? 0,
      pendingEarnings: dto.stats?.pendingEarnings ?? 0,
      totalEarnings: dto.stats?.totalEarnings ?? 0,
      availableForPayout: dto.stats?.availableForPayout ?? 0,
      lifetimePayouts: dto.stats?.lifetimePayouts ?? 0,
    };

    const referrals: Referral[] = (dto.referrals ?? []).map((r) => ({
      id: r.id ?? '',
      referredEmail: r.referredEmail ?? '',
      referredName: r.referredName ?? '',
      signupDate: r.signupDate ? new Date(r.signupDate) : null,
      status: r.status ?? 'pending',
      earnedAmount: r.earnedAmount ?? 0,
      qualificationProgress: r.qualificationProgress ?? 0,
      conversionDate: r.conversionDate ? new Date(r.conversionDate) : undefined,
    }));

    return {
      isEnrolled: dto.isEnrolled ?? false,
      referralLink: dto.referralLink ?? null,
      referralCode: dto.referralCode ?? null,
      stats,
      referrals,
      payPalEmail: dto.payPalEmail ?? null,
    };
  }

  /**
   * Update the PayPal email stored on CompanySettings.
   * Note: this is NOT synced to FirstPromoter (API constraint — see spec).
   * Payouts are executed manually by the Routemize team.
   */
  async updatePayPalEmail(email: string): Promise<void> {
    const body: Routemize_DTOs_UpdatePayPalEmailDto = { email };
    await ReferralService.putApiAppReferralUpdatePayPalEmail(body);
  }

  // ============================================================================
  // Utility helpers
  // ============================================================================

  /** Format a dollar amount as USD currency. */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  /** Format a Date for display (e.g. "Jan 15, 2026"). */
  formatDate(date: Date | null | undefined): string {
    if (!date) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  /**
   * Normalize an arbitrary FirstPromoter status string into display-safe UI
   * tokens. Unknown values fall back to a neutral style rather than erroring.
   */
  getStatusDisplay(status: string): { text: string; color: string; bgColor: string } {
    const normalized = (status ?? '').toLowerCase();
    // FirstPromoter referral states (v2): signup, subscribed, active, cancelled,
    // refunded, denied, pending, moved.
    const statusMap: Record<string, { text: string; color: string; bgColor: string }> = {
      signup:     { text: 'Signed up',  color: 'text-accent-700',  bgColor: 'bg-accent-50'   },
      pending:    { text: 'Pending',    color: 'text-warning-700', bgColor: 'bg-warning-50'  },
      subscribed: { text: 'Subscribed', color: 'text-success-700', bgColor: 'bg-success-50'  },
      active:     { text: 'Active',     color: 'text-success-700', bgColor: 'bg-success-50'  },
      cancelled:  { text: 'Cancelled',  color: 'text-ink-600',     bgColor: 'bg-ink-100'     },
      refunded:   { text: 'Refunded',   color: 'text-danger-700',  bgColor: 'bg-danger-50'   },
      denied:     { text: 'Denied',     color: 'text-danger-700',  bgColor: 'bg-danger-50'   },
      moved:      { text: 'Moved',      color: 'text-ink-600',     bgColor: 'bg-ink-100'     },
    };
    // Unknown value: title-case it rather than erroring, neutral style.
    const fallbackText = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    return statusMap[normalized] ?? { text: fallbackText, color: 'text-ink-600', bgColor: 'bg-ink-100' };
  }
}

export const referralManager = new ReferralManager();
